import { useState } from "react";
import { useLocation } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { SubTabBar } from "@/components/SubTabBar";
import { allScreens, groupByMainTab, findScreenByRoute } from "@/application/navigation";
import type { PortalId } from "@/domain/screen";
import { fetchSheetPreview } from "@/repositories/sheetPreviewRepository";

export function ScreenPage({ portal }: { portal: PortalId }) {
  const { pathname } = useLocation();
  const screen = findScreenByRoute(pathname) ?? allScreens.find((s) => s.portal === portal);

  const [preview, setPreview] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!screen) {
    return (
      <AppShell portal={portal}>
        <div className="placeholder-page">
          <h1>Screen not found in Screen-to-Data Map</h1>
        </div>
      </AppShell>
    );
  }

  const siblingScreens = groupByMainTab(portal).find((g) => g.mainTab === screen.mainTab)?.screens ?? [];

  async function handleLoadData() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSheetPreview(screen!.readsFrom, 5);
      setPreview(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AppShell portal={portal}>
      <div className="placeholder-page">
        <div className="breadcrumb">
          {screen.portal} / {screen.mainTab}
          {screen.subTab ? ` / ${screen.subTab}` : ""}
        </div>
        <SubTabBar screens={siblingScreens} />

        <div className="card">
          <h1>{screen.subTab ?? screen.mainTab}</h1>
          <p style={{ color: "var(--text-muted)", fontSize: 14 }}>
            {screen.notes ?? "No notes in Screen-to-Data Map."} · Route: <code>{screen.route}</code>
          </p>

          <div style={{ marginTop: 14 }}>
            <strong style={{ fontSize: 13 }}>Reads from:</strong>
            <div className="data-tag-list">
              {screen.readsFrom.map((s) => (
                <span key={s} className="data-tag">{s}</span>
              ))}
            </div>
          </div>

          {screen.writesTo.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <strong style={{ fontSize: 13 }}>Writes to (disabled while read-only):</strong>
              <div className="data-tag-list">
                {screen.writesTo.map((s) => (
                  <span key={s} className="data-tag">{s}</span>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 20 }}>
            <button onClick={handleLoadData} disabled={loading}>
              {loading ? "Loading live data…" : "Load live data from Google Sheets"}
            </button>
          </div>

          {error && <p style={{ color: "#f87171", marginTop: 12 }}>{error}</p>}
          {preview !== null && (
            <pre className="debug-json" style={{ marginTop: 16 }}>
              {JSON.stringify(preview, null, 2)}
            </pre>
          )}
        </div>

        <p style={{ marginTop: 16, fontSize: 12, color: "var(--text-muted)" }}>
          This is a foundation-phase placeholder. The real approved visual design for this
          screen (reference: <code>{screen.reference}</code>) will replace this card in the
          next implementation phase, matched pixel-for-pixel against the canonical reference image.
        </p>
      </div>
    </AppShell>
  );
}
